import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Edit2, ExternalLink, Plus, X, XCircle, Search, GitMerge, Building2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ImageUpload } from "@/components/ImageUpload";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface AdminVenue {
  id: number;
  name: string;
  slug: string | null;
  venueType: string;
  description: string | null;
  town: string | null;
  address: string | null;
  postcode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  facebook: string | null;
  instagram: string | null;
  twitter: string | null;
  isActive: boolean;
  isFeatured: boolean;
  approved: boolean;
  amenities: string[];
  coverPhoto: string | null;
  happyHourText: string | null;
  foodInfo: string | null;
  accessibilityInfo: string | null;
  ageRestriction: string | null;
  createdAt: string;
}

type Tab = "pending" | "live" | "all";

const VENUE_TYPES = [
  "pub", "bar", "nightclub", "restaurant", "live_music_venue",
  "social_club", "sports_bar", "cocktail_bar", "other",
];

// Legacy free-text amenity tokens (bar_listings.amenities). Kept because live
// rows still carry them, but the structured vocabulary below (venue_facilities
// joined to facility_definitions) is the model new work should use.
const AMENITIES_LIST = [
  { value: "pool_table",   label: "Pool Table 🎱" },
  { value: "darts",        label: "Darts Board 🎯" },
  { value: "beer_garden",  label: "Beer Garden 🌿" },
  { value: "sports_tv",    label: "Sports TV 📺" },
  { value: "karaoke_rig",  label: "Karaoke 🎤" },
  { value: "stage",        label: "Live Stage 🎸" },
  { value: "live_sport",   label: "Live Sport 🏆" },
  { value: "outdoor_area", label: "Outdoor Area ☀️" },
  { value: "private_hire", label: "Private Hire 👥" },
  { value: "dog_friendly", label: "Dog Friendly 🐶" },
  { value: "accessible",   label: "Accessible ♿" },
  { value: "parking",      label: "Parking 🅿️" },
  { value: "food_served",  label: "Food Served 🍽️" },
  { value: "jukebox",      label: "Jukebox 🎵" },
];

const BLANK_FORM = {
  name: "", description: "", venueType: "pub",
  address: "", town: "", postcode: "",
  phone: "", email: "", website: "",
  facebook: "", instagram: "", twitter: "",
  amenities: [] as string[],
  coverPhoto: "",
  happyHourText: "", foodInfo: "", accessibilityInfo: "", ageRestriction: "",
  isFeatured: false, isActive: false, approved: false,
};

type VenueForm = typeof BLANK_FORM;

function toForm(v: AdminVenue): VenueForm {
  return {
    name: v.name ?? "",
    description: v.description ?? "",
    venueType: v.venueType ?? "pub",
    address: v.address ?? "",
    town: v.town ?? "",
    postcode: v.postcode ?? "",
    phone: v.phone ?? "",
    email: v.email ?? "",
    website: v.website ?? "",
    facebook: v.facebook ?? "",
    instagram: v.instagram ?? "",
    twitter: v.twitter ?? "",
    amenities: v.amenities ?? [],
    coverPhoto: v.coverPhoto ?? "",
    happyHourText: v.happyHourText ?? "",
    foodInfo: v.foodInfo ?? "",
    accessibilityInfo: v.accessibilityInfo ?? "",
    ageRestriction: v.ageRestriction ?? "",
    isFeatured: v.isFeatured,
    isActive: v.isActive,
    approved: v.approved,
  };
}

// ─── Structured facilities (venue_facilities × facility_definitions) ──────────
// This is the real model. It replaces free text with a controlled vocabulary so
// "which pubs have a pool table?" is answerable by SQL. Rows are soft-deleted
// (is_active = false) by the API, never removed.

interface FacilityDefinition {
  id: number;
  name: string;
  slug: string;
  category: string;
  isActive: boolean;
}

interface VenueFacility {
  id: number;
  facilityDefinitionId: number;
  notes: string | null;
  isActive: boolean;
  definition: { name: string; slug: string; category: string; icon: string | null };
}

function FacilitiesEditor({ venueId }: { venueId: number }) {
  const { toast } = useToast();
  const [definitions, setDefinitions] = useState<FacilityDefinition[]>([]);
  const [assigned, setAssigned] = useState<VenueFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyDefId, setBusyDefId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defRes, venRes] = await Promise.all([
        fetch("/api/facility-definitions", { headers: authHeaders() }),
        fetch(`/api/venues/${venueId}/facilities`, { headers: authHeaders() }),
      ]);
      const defs = await defRes.json() as { items?: FacilityDefinition[] };
      const mine = await venRes.json() as { items?: VenueFacility[] };
      setDefinitions(defs.items ?? []);
      setAssigned(mine.items ?? []);
    } catch {
      toast({ title: "Failed to load facilities", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [venueId, toast]);

  useEffect(() => { void load(); }, [load]);

  // GET /venues/:id/facilities only returns active rows, so anything not listed
  // is either absent or previously soft-deleted — both are re-added by POST,
  // and a 409 means a deactivated row exists, which we then reactivate.
  const assignedByDef = useMemo(
    () => new Map(assigned.map((a) => [a.facilityDefinitionId, a])),
    [assigned],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, FacilityDefinition[]>();
    for (const d of definitions) {
      const bucket = map.get(d.category);
      if (bucket) bucket.push(d);
      else map.set(d.category, [d]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [definitions]);

  const toggle = async (def: FacilityDefinition) => {
    const existing = assignedByDef.get(def.id);
    setBusyDefId(def.id);
    try {
      if (existing) {
        const res = await fetch(`/api/venues/${venueId}/facilities/${existing.id}`, {
          method: "DELETE",
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error("Failed to remove facility");
      } else {
        const res = await fetch(`/api/venues/${venueId}/facilities`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ facilityDefinitionId: def.id }),
        });
        if (res.status === 409) {
          // A soft-deleted row already exists — reactivate it rather than
          // creating a second one.
          const allRes = await fetch(`/api/admin/venue-model/venues/${venueId}/context`, { headers: authHeaders() });
          const ctx = await allRes.json() as { facilities?: { id: number; facilityDefinitionId: number }[] };
          const row = ctx.facilities?.find((f) => f.facilityDefinitionId === def.id);
          if (!row) throw new Error("Facility already assigned");
          const put = await fetch(`/api/venues/${venueId}/facilities/${row.id}`, {
            method: "PUT",
            headers: authHeaders(),
            body: JSON.stringify({ isActive: true }),
          });
          if (!put.ok) throw new Error("Failed to reactivate facility");
        } else if (!res.ok) {
          throw new Error("Failed to add facility");
        }
      }
      await load();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed", variant: "destructive" });
    } finally {
      setBusyDefId(null);
    }
  };

  if (loading) return <p className="text-xs text-slate-400">Loading facilities…</p>;
  if (definitions.length === 0) {
    return <p className="text-xs text-slate-400">No facility definitions exist yet — add some under Facilities &amp; Activities.</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-slate-500">
        {assigned.length} facilit{assigned.length === 1 ? "y" : "ies"} assigned. These write to the
        <code className="mx-1 font-mono">venue_facilities</code> join table — not free text.
      </p>
      {grouped.map(([category, defs]) => (
        <div key={category}>
          <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-1.5">{category}</div>
          <div className="flex flex-wrap gap-2">
            {defs.map((d) => {
              const active = assignedByDef.has(d.id);
              return (
                <button
                  key={d.id}
                  type="button"
                  disabled={busyDefId === d.id}
                  onClick={() => void toggle(d)}
                  title={d.slug}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors disabled:opacity-40 ${
                    active
                      ? "bg-primary text-white border-primary"
                      : "border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary"
                  }`}
                >
                  {d.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Ownership (reuses the existing business ownership system) ────────────────

interface BusinessResult {
  id: number; name: string; town: string; category: string;
  ownerId: number | null; ownerName: string | null; ownerEmail: string | null;
}
interface UserResult { id: number; name: string; email: string; role: string }

const businessSearchUrl = (q: string) => `/api/admin/venue-model/businesses/search?q=${encodeURIComponent(q)}`;
const userSearchUrl = (q: string) => `/api/admin/users/search?q=${encodeURIComponent(q)}`;

function useDebouncedSearch<T>(url: (q: string) => string, key: string) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback((s: string) => {
    setQ(s);
    if (timer.current) clearTimeout(timer.current);
    if (s.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(url(s.trim()), { headers: authHeaders() });
        const d = await r.json() as Record<string, T[]>;
        setResults(d[key] ?? []);
      } catch { /* ignore */ }
    }, 300);
  }, [url, key]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return { q, setQ, results, setResults, search };
}

interface VenueOwnershipContext {
  business: { id: number; name: string; ownerId: number | null; ownerName: string | null; ownerEmail: string | null } | null;
  manager: { id: number; name: string; email: string } | null;
}

function OwnershipEditor({ venueId, onChanged }: { venueId: number; onChanged: () => void }) {
  const { toast } = useToast();
  const [ctx, setCtx] = useState<VenueOwnershipContext | null>(null);
  const [saving, setSaving] = useState(false);

  const biz = useDebouncedSearch<BusinessResult>(businessSearchUrl, "items");
  const usr = useDebouncedSearch<UserResult>(userSearchUrl, "users");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/admin/venue-model/venues/${venueId}/context`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json() as VenueOwnershipContext;
      setCtx({ business: d.business ?? null, manager: d.manager ?? null });
    } catch { /* ignore */ }
  }, [venueId]);

  useEffect(() => { void load(); }, [load]);

  const patch = async (body: Record<string, unknown>, message: string) => {
    setSaving(true);
    try {
      const r = await fetch(`/api/admin/venue-model/venues/${venueId}/ownership`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? "Failed");
      }
      toast({ title: message });
      biz.setQ(""); biz.setResults([]);
      usr.setQ(""); usr.setResults([]);
      await load();
      onChanged();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Business link */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">Owning business</label>
        {ctx?.business ? (
          <div className="flex items-center gap-2 flex-wrap text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <Building2 size={13} className="text-slate-400" />
            <span className="font-medium text-slate-800">{ctx.business.name}</span>
            <span className="text-xs text-slate-400">
              {ctx.business.ownerName ? `owner: ${ctx.business.ownerName}` : "no owner on the business record"}
            </span>
            <button
              type="button"
              disabled={saving}
              onClick={() => void patch({ businessId: null }, "Business unlinked")}
              className="ml-auto text-xs text-slate-400 hover:text-red-600 disabled:opacity-50"
            >
              Unlink
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              value={biz.q}
              onChange={(e) => biz.search(e.target.value)}
              placeholder="Search businesses by name…"
              className="h-8 text-sm"
            />
            {biz.results.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                {biz.results.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    onClick={() => void patch({ businessId: b.id }, `Linked to "${b.name}"`)}
                  >
                    <span className="font-medium">{b.name}</span>
                    <span className="text-slate-400 ml-2 text-xs">
                      {b.town} · {b.ownerName ?? "no owner"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          Linking a business with an owner also makes that user the venue manager when none is set.
        </p>
      </div>

      {/* Manager (bar_listings.created_by — what the venue APIs check for write access) */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">Venue manager (portal access)</label>
        {ctx?.manager ? (
          <div className="flex items-center gap-2 flex-wrap text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <ShieldCheck size={13} className="text-slate-400" />
            <span className="font-medium text-slate-800">{ctx.manager.name}</span>
            <span className="text-xs text-slate-400">{ctx.manager.email}</span>
            <button
              type="button"
              disabled={saving}
              onClick={() => void patch({ createdBy: null }, "Manager removed")}
              className="ml-auto text-xs text-slate-400 hover:text-red-600 disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="relative">
            <Input
              value={usr.q}
              onChange={(e) => usr.search(e.target.value)}
              placeholder="Search users by name or email…"
              className="h-8 text-sm"
            />
            {usr.results.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                {usr.results.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    onClick={() => void patch({ createdBy: u.id }, `${u.name} is now the venue manager`)}
                  >
                    <span className="font-medium">{u.name}</span>
                    <span className="text-slate-400 ml-2 text-xs">{u.email} · {u.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          This is <code className="font-mono">bar_listings.created_by</code> — the field the venue,
          facility and activity APIs already check before allowing a non-admin to edit.
        </p>
      </div>
    </div>
  );
}

function VenueEditPanel({
  form,
  set,
  onSave,
  onCancel,
  saving,
  isNew,
  venueId,
  onOwnershipChanged,
}: {
  form: VenueForm;
  set: (k: keyof VenueForm, v: unknown) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isNew: boolean;
  venueId: number | null;
  onOwnershipChanged: () => void;
}) {
  const toggleAmenity = (val: string) =>
    set("amenities", form.amenities.includes(val)
      ? form.amenities.filter((a) => a !== val)
      : [...form.amenities, val]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 pt-4 pb-8 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800">{isNew ? "Register New Venue" : "Edit Venue"}</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[80vh]">
          {/* Cover Photo */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Cover Photo</h3>
            <ImageUpload
              value={form.coverPhoto}
              onChange={(url) => set("coverPhoto", url)}
              label="Upload Cover Photo"
              aspect={16 / 9}
              enableCrop
            />
          </div>

          {/* Basic Info */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Basic Info</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Venue Name *</label>
                <Input value={form.name} onChange={e => set("name", e.target.value)} className="h-8 text-sm" placeholder="e.g. The Howard Arms" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Venue Type</label>
                <select value={form.venueType} onChange={e => set("venueType", e.target.value)} className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background capitalize">
                  {VENUE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => set("description", e.target.value)}
                  className="w-full text-sm border border-input rounded-md px-3 py-2 min-h-[70px] resize-y bg-background"
                  placeholder="About the venue…"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Age Restriction</label>
                <Input value={form.ageRestriction} onChange={e => set("ageRestriction", e.target.value)} className="h-8 text-sm" placeholder="e.g. 18+" />
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Location</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-500 mb-1">Address</label>
                <Input value={form.address} onChange={e => set("address", e.target.value)} className="h-8 text-sm" placeholder="Street address" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
                <Input value={form.town} onChange={e => set("town", e.target.value)} className="h-8 text-sm" placeholder="e.g. Kilmarnock" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Postcode</label>
                <Input value={form.postcode} onChange={e => set("postcode", e.target.value)} className="h-8 text-sm" placeholder="KA1 1AB" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Contact</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Phone</label>
                <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="h-8 text-sm" placeholder="01563 xxxxxx" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Email</label>
                <Input value={form.email} onChange={e => set("email", e.target.value)} className="h-8 text-sm" type="email" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Website</label>
                <Input value={form.website} onChange={e => set("website", e.target.value)} className="h-8 text-sm" placeholder="https://…" />
              </div>
            </div>
          </div>

          {/* Social */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Social Media</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Facebook</label>
                <Input value={form.facebook} onChange={e => set("facebook", e.target.value)} className="h-8 text-sm" placeholder="facebook.com/…" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Instagram</label>
                <Input value={form.instagram} onChange={e => set("instagram", e.target.value)} className="h-8 text-sm" placeholder="@handle" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Twitter / X</label>
                <Input value={form.twitter} onChange={e => set("twitter", e.target.value)} className="h-8 text-sm" placeholder="@handle" />
              </div>
            </div>
          </div>

          {/* Structured facilities — the venue_facilities model */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Facilities</h3>
            {venueId == null ? (
              <p className="text-xs text-slate-400">
                Save the venue first — facilities attach to a venue id.
              </p>
            ) : (
              <FacilitiesEditor venueId={venueId} />
            )}
          </div>

          {/* Ownership */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Ownership</h3>
            {venueId == null ? (
              <p className="text-xs text-slate-400">Save the venue first, then assign an owner.</p>
            ) : (
              <OwnershipEditor venueId={venueId} onChanged={onOwnershipChanged} />
            )}
          </div>

          {/* Legacy amenities */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-1">Legacy amenities</h3>
            <p className="text-[11px] text-slate-400 mb-3">
              Free-text <code className="font-mono">bar_listings.amenities</code>. Kept for backward
              compatibility with older app builds — use Facilities above for anything new.
            </p>
            <div className="flex flex-wrap gap-2">
              {AMENITIES_LIST.map((a) => {
                const active = form.amenities.includes(a.value);
                return (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => toggleAmenity(a.value)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? "bg-primary text-white border-primary"
                        : "border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary"
                    }`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Extra */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Extra Info</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Happy Hour / Deals</label>
                <Input value={form.happyHourText} onChange={e => set("happyHourText", e.target.value)} className="h-8 text-sm" placeholder="e.g. Mon–Fri 5–7pm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Food Info</label>
                <Input value={form.foodInfo} onChange={e => set("foodInfo", e.target.value)} className="h-8 text-sm" placeholder="e.g. Bar snacks served daily" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Accessibility</label>
                <Input value={form.accessibilityInfo} onChange={e => set("accessibilityInfo", e.target.value)} className="h-8 text-sm" placeholder="e.g. Step-free access" />
              </div>
            </div>
          </div>

          {/* Admin flags */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Admin Flags</h3>
            <div className="flex flex-wrap gap-4">
              {([["isActive", "Active / Live"], ["approved", "Approved"], ["isFeatured", "Featured"]] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form[key] as boolean} onChange={e => set(key, e.target.checked)} className="rounded" />
                  <span className="text-sm text-slate-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving || !form.name.trim()}
            className="px-5 py-2 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : isNew ? "Register Venue" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminVenues() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Tab>("all");
  const [actingId, setActingId] = useState<number | null>(null);
  const [editingVenue, setEditingVenue] = useState<AdminVenue | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editForm, setEditForm] = useState<VenueForm>(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [townFilter, setTownFilter] = useState("");

  // NB: hooks must run before the auth guards below — an early return above a
  // hook changes the hook count between renders and crashes React.
  const isAdmin = user?.role === "admin";

  const { data, isLoading } = useQuery<{ items: AdminVenue[]; total: number }>({
    queryKey: ["admin-venues"],
    enabled: isAdmin,
    queryFn: async () => {
      // The admin list is paginated server-side (default 50, max 100).
      const res = await fetch("/api/admin/venues?limit=100", { headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to load venues");
      return res.json() as Promise<{ items: AdminVenue[]; total: number }>;
    },
  });

  const allVenues = useMemo(() => data?.items ?? [], [data]);

  const towns = useMemo(
    () => [...new Set(allVenues.map((v) => v.town).filter((t): t is string => !!t))].sort(),
    [allVenues],
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return allVenues
      .filter((v) => !s || v.name.toLowerCase().includes(s) || (v.town ?? "").toLowerCase().includes(s))
      .filter((v) => !townFilter || v.town === townFilter)
      .filter((v) => (tab === "pending" ? !v.isActive : tab === "live" ? v.isActive : true));
  }, [allVenues, search, townFilter, tab]);

  const pendingCount = useMemo(() => allVenues.filter((v) => !v.isActive).length, [allVenues]);

  if (authLoading) return null;
  if (!isAdmin) return <Redirect to="/" />;

  const set = (k: keyof VenueForm, v: unknown) => setEditForm(p => ({ ...p, [k]: v }));

  const openEdit = (venue: AdminVenue) => {
    setEditingVenue(venue);
    setEditForm(toForm(venue));
    setShowCreate(false);
  };

  const openCreate = () => {
    setEditingVenue(null);
    setEditForm(BLANK_FORM);
    setShowCreate(true);
  };

  const closePanel = () => { setEditingVenue(null); setShowCreate(false); };

  const handleSave = async () => {
    if (!editForm.name.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        venueType: editForm.venueType,
        address: editForm.address.trim() || null,
        town: editForm.town.trim() || null,
        postcode: editForm.postcode.trim() || null,
        phone: editForm.phone.trim() || null,
        email: editForm.email.trim() || null,
        website: editForm.website.trim() || null,
        facebook: editForm.facebook.trim() || null,
        instagram: editForm.instagram.trim() || null,
        twitter: editForm.twitter.trim() || null,
        amenities: editForm.amenities,
        coverPhoto: editForm.coverPhoto.trim() || null,
        happyHourText: editForm.happyHourText.trim() || null,
        foodInfo: editForm.foodInfo.trim() || null,
        accessibilityInfo: editForm.accessibilityInfo.trim() || null,
        ageRestriction: editForm.ageRestriction.trim() || null,
        isActive: editForm.isActive,
        approved: editForm.approved,
        isFeatured: editForm.isFeatured,
      };

      if (showCreate) {
        const res = await fetch("/api/bars", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error("Create failed");
        const created = await res.json() as AdminVenue;
        // Admin-only flags need a PUT after creation (POST hardcodes isActive/approved false)
        if (editForm.isActive || editForm.approved || editForm.isFeatured) {
          await fetch(`/api/venues/${created.id}`, {
            method: "PUT",
            headers: authHeaders(),
            body: JSON.stringify({ isActive: editForm.isActive, approved: editForm.approved, isFeatured: editForm.isFeatured }),
          });
        }
        toast({ title: `"${editForm.name}" registered` });
      } else if (editingVenue) {
        const res = await fetch(`/api/venues/${editingVenue.id}`, {
          method: "PUT",
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error("Save failed");
        toast({ title: `"${editForm.name}" updated` });
      }

      await queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
      closePanel();
    } catch {
      toast({ title: "Failed to save venue", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (venue: AdminVenue) => {
    setActingId(venue.id);
    try {
      const res = await fetch(`/api/admin/venues/${venue.id}/approve`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to approve");
      toast({ title: `"${venue.name}" approved and now live` });
      await queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
    } catch {
      toast({ title: "Failed to approve venue", variant: "destructive" });
    } finally {
      setActingId(null);
    }
  };

  const handleDeactivate = async (venue: AdminVenue) => {
    if (!window.confirm(`Deactivate "${venue.name}"? It will be hidden from the public.`)) return;
    setActingId(venue.id);
    try {
      const res = await fetch(`/api/admin/venues/${venue.id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to deactivate");
      toast({ title: `"${venue.name}" deactivated` });
      await queryClient.invalidateQueries({ queryKey: ["admin-venues"] });
    } catch {
      toast({ title: "Failed to deactivate venue", variant: "destructive" });
    } finally {
      setActingId(null);
    }
  };

  const TABS: { key: Tab; label: string }[] = [
    { key: "pending", label: "Pending Approval" },
    { key: "live", label: "Live" },
    { key: "all", label: "All" },
  ];

  return (
    <AdminLayout current="/admin/venues">
      {(editingVenue || showCreate) && (
        <VenueEditPanel
          form={editForm}
          set={set}
          onSave={() => void handleSave()}
          onCancel={closePanel}
          saving={saving}
          isNew={showCreate}
          venueId={editingVenue?.id ?? null}
          onOwnershipChanged={() => void queryClient.invalidateQueries({ queryKey: ["admin-venues"] })}
        />
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-primary">Venues</h1>
          <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2.5 py-1 rounded-full">
            {allVenues.length}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link
              href="/admin/venue-review"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:border-primary/40 hover:text-primary transition-colors"
            >
              <GitMerge size={14} /> Review &amp; merge
            </Link>
            <button
              onClick={openCreate}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors"
            >
              <Plus size={14} /> Register New Venue
            </button>
          </div>
        </div>

        {/* Search + town filter */}
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or town…"
              className="w-full pl-8 pr-4 py-2 text-sm border border-slate-200 rounded-xl outline-none focus:border-primary/40 bg-white"
            />
          </div>
          <select
            value={townFilter}
            onChange={(e) => setTownFilter(e.target.value)}
            className="h-[38px] text-sm border border-slate-200 rounded-xl px-3 bg-white outline-none focus:border-primary/40"
          >
            <option value="">All towns</option>
            {towns.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {(search || townFilter) && (
            <button
              onClick={() => { setSearch(""); setTownFilter(""); }}
              className="text-xs text-slate-400 hover:text-slate-600 px-2"
            >
              Clear
            </button>
          )}
          <span className="text-xs text-slate-400 ml-auto">
            Showing {filtered.length} of {allVenues.length}
            {(data?.total ?? 0) > allVenues.length && (
              <span className="text-amber-600"> · {data?.total} exist, first 100 loaded</span>
            )}
          </span>
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 border-b border-slate-200">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                tab === key
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              {label}
              {key === "pending" && pendingCount > 0 && (
                <span className="bg-orange-100 text-orange-700 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="text-slate-400 text-sm py-8 text-center">Loading venues…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">No venues found.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Town</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Amenities</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600 text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((venue) => (
                  <tr key={venue.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-800">{venue.name}</span>
                      {venue.isFeatured && (
                        <span className="ml-2 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                          Featured
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 capitalize">{venue.venueType?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-slate-500">{venue.town ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {venue.amenities.length > 0 ? (
                        <span title={venue.amenities.join(", ")}>
                          {venue.amenities.length} amenit{venue.amenities.length === 1 ? "y" : "ies"}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {venue.isActive ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <CheckCircle size={11} /> Live
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">
                          <XCircle size={11} /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 justify-end flex-wrap">
                        <button
                          onClick={() => openEdit(venue)}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors"
                        >
                          <Edit2 size={11} /> Edit
                        </button>
                        {!venue.isActive && (
                          <button
                            onClick={() => void handleApprove(venue)}
                            disabled={actingId === venue.id}
                            className="text-xs font-medium px-2.5 py-1 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                          >
                            Approve
                          </button>
                        )}
                        {venue.isActive && (
                          <button
                            onClick={() => void handleDeactivate(venue)}
                            disabled={actingId === venue.id}
                            className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
                          >
                            Deactivate
                          </button>
                        )}
                        <Link
                          href={venue.slug ? `/venues/${venue.slug}` : `/venues/${venue.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 hover:border-primary/40 hover:text-primary transition-colors"
                        >
                          <ExternalLink size={11} /> View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
